/**
 * @fileoverview Member Escalation API Module
 * Provides the API calls for listing, creating, and updating a member's
 * escalations from the Member Outreach member page (L9 only).
 *
 * The UI works with a domain shape (escalationType, escalationReason,
 * description, status, resolution, ownerId, ownerName) that's translated
 * here to/from the backend's MemberEscalation*Request/Response contract
 * (escalationNote, escalationStatus, escalationResolution, ...), so
 * components never need to know the wire format.
 *
 * Contact attempts (who/when/how an escalation's contact was reached) are a
 * separate sub-resource under /escalations/{id}/contact-attempts, not flat
 * fields on the escalation record - see createContactAttempt/
 * fetchContactAttempts.
 */

import api from "./api";
import { getCurrentUser } from "../utils/auth";

const HEADERS = {
  "Content-Type": "application/json",
  "ngrok-skip-browser-warning": "true",
};

// MemberEscalationResponse -> domain shape used by the UI. /history entries
// only carry ownerId (no ownerName), unlike the current-record and list
// endpoints - ownerNameById lets any caller backfill the name from the same
// L9 roster used for the Owner dropdown.
//
// escalationId is the actual escalation's id regardless of which endpoint raw
// came from: MemberEscalationResponse (current/list) only has its own id,
// but MemberEscalationHistoryResponse's id is that history row's own PK -
// its memberEscalationId is the one that points back to the live escalation.
// Callers needing to address "this escalation" (e.g. the contact-attempts
// sub-resource) from either a current or historical snapshot should use
// escalationId, not id.
//
// contactStatus describes the outcome of a contact attempt, but the attempts
// themselves (contactAt/contactParty/contactMethod) are no longer flat
// scalars here - they're their own sub-resource, see
// createContactAttempt/fetchContactAttempts.
// resolutionBy/resolutionDatetime aren't backend-computed - updateEscalation
// stamps them itself the moment a status change to Resolved is submitted.
const mapEscalationResponse = (raw, ownerNameById) => ({
  id: raw.id,
  escalationId: raw.memberEscalationId ?? raw.id,
  memberId: raw.memberId,
  escalationType: raw.escalationType,
  escalationReason: raw.escalationReason,
  status: raw.escalationStatus,
  priority: raw.escalationPriority,
  description: raw.escalationNote,
  followup: raw.escalationFollowup,
  contactType: raw.escalationContact,
  contactStatus: raw.contactStatus,
  followUpNeeded: raw.followUpNeeded,
  followUpDate: raw.followUpDate,
  resolution: raw.escalationResolution,
  resolutionBy: raw.resolutionBy,
  resolutionDatetime: raw.resolutionDatetime,
  cancellationReason: raw.cancelReason,
  ownerId: raw.ownerId,
  ownerName: raw.ownerName || ownerNameById?.get(String(raw.ownerId)) || "",
  assigneeId: raw.assigneeId,
  assigneeName: raw.assigneeName,
  createdAt: raw.createdAt,
  createdBy: raw.createdBy,
  updatedAt: raw.updatedAt,
  updatedBy: raw.updatedBy,
});

// MemberEscalationContactAttemptResponse -> domain shape used by the UI.
const mapContactAttemptResponse = (raw) => ({
  id: raw.id,
  contactAt: raw.contactAt,
  contactParty: raw.contactParty,
  contactMethod: raw.contactMethod,
});

const getCurrentUserId = () => {
  const currentUser = getCurrentUser();
  return currentUser?.id ?? currentUser?.employeeId ?? undefined;
};

// The backend parses datetime strings like a Java LocalDateTime - no UTC
// offset/"Z" suffix (same convention as contactAt elsewhere in this API,
// which is built from local <input type="date"/"time"> values) - so "now"
// has to be formatted from local wall-clock components rather than
// Date#toISOString, which always emits a trailing "Z" the backend rejects.
const nowAsLocalDateTime = () => {
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
};

// Used to stamp resolutionBy when an update resolves the escalation -
// resolutionBy is always the submitting user (assigneeId), so their own
// profile is resolved directly rather than through the L9 owner lookup.
const getCurrentUserName = () => {
  const currentUser = getCurrentUser();
  const name =
    `${currentUser?.firstName || ""} ${currentUser?.lastName || ""}`.trim();
  return (
    name ||
    currentUser?.emailId ||
    currentUser?.email ||
    String(getCurrentUserId() ?? "")
  );
};

// The Status dropdown is available on both create and update - either can be
// the one that first sets status to "Resolved" (creating an escalation that's
// already resolved is a valid retroactive-logging flow), so both need to
// stamp who/when. Once resolved, the record locks (see
// TERMINAL_ESCALATION_STATUSES/readOnly in MemberDetailsPage), so this can
// never fire again on a later save of the same escalation.
const resolutionStampIfResolving = (payload) =>
  payload.status === "Resolved"
    ? {
        resolutionBy: getCurrentUserName(),
        resolutionDatetime: nowAsLocalDateTime(),
      }
    : {};

// Domain shape from CreateEscalationModal -> MemberEscalationCreate/UpdateRequest.
const buildEscalationRequestBody = (payload) => ({
  ownerId: payload.ownerId,
  escalationType: payload.escalationType,
  escalationReason: payload.escalationReason,
  escalationStatus: payload.status,
  escalationPriority: payload.priority,
  escalationNote: payload.description,
  escalationFollowup: payload.followup,
  escalationContact: payload.contactType,
  contactStatus: payload.contactStatus,
  followUpNeeded: payload.followUpNeeded,
  followUpDate: payload.followUpDate,
  escalationResolution: payload.resolution,
  cancelReason: payload.cancellationReason,
});

// The L9 roster only changes when roles are reassigned, which doesn't
// happen mid-session - cache it so every escalation list/history fetch
// doesn't refetch the full employee directory just to backfill owner names.
let l9UsersCache = null;

// Reuses the same /api/employees listing the user/role management screen
// (src/components/manage-permissions/user-list) fetches from, filtered down
// to L9s for the escalation Owner dropdown.
export const fetchL9Users = async () => {
  if (l9UsersCache) {
    return l9UsersCache;
  }

  l9UsersCache = (async () => {
    try {
      const response = await api.get("/api/employees", { headers: HEADERS });
      const data = response.data;
      const list = Array.isArray(data) ? data : data?.content || [];

      return list
        .filter((employee) =>
          (employee.roles || []).some(
            (role) => (role.roleName || role.name || "").toUpperCase() === "L9",
          ),
        )
        .map((employee) => ({
          id: employee.id,
          name:
            `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
            employee.emailId,
        }));
    } catch (err) {
      l9UsersCache = null;
      throw err;
    }
  })();

  return l9UsersCache;
};

export const clearL9UsersCache = () => {
  l9UsersCache = null;
};

const getOwnerNameLookup = async () => {
  const users = await fetchL9Users();
  return new Map(users.map((user) => [String(user.id), user.name]));
};

export const fetchEscalations = async (memberId) => {
  const [response, ownerNameById] = await Promise.all([
    api.get(`/api/members/${memberId}/escalations`, { headers: HEADERS }),
    getOwnerNameLookup(),
  ]);
  const list = Array.isArray(response.data) ? response.data : [];
  return list.map((raw) => mapEscalationResponse(raw, ownerNameById));
};

// Fetches every prior version (zero or more) and merges them with the
// already-known current record into a single list, oldest first by
// updatedAt (history entries can share the same createdAt as the original
// record, so createdAt can't order them) - the current record always comes
// last regardless of its own timestamps, so it doesn't get sorted in among
// the history entries. currentEscalation is the list item the caller
// already has from fetchEscalations - it's the same record a GET on
// /escalations/{id} would return, so reusing it here skips a redundant
// fetch. The current record is tagged isCurrent so callers know which one
// is still editable - everything from /history is an immutable past
// snapshot.
export const fetchEscalationVersions = async (
  memberId,
  escalationId,
  currentEscalation,
) => {
  const [historyResponse, ownerNameById] = await Promise.all([
    api.get(`/api/members/${memberId}/escalations/${escalationId}/history`, {
      headers: HEADERS,
    }),
    getOwnerNameLookup(),
  ]);

  const current = { ...currentEscalation, isCurrent: true };
  const historyList = Array.isArray(historyResponse.data)
    ? historyResponse.data
    : [];
  const history = historyList
    .map((raw) => ({
      ...mapEscalationResponse(raw, ownerNameById),
      isCurrent: false,
    }))
    .sort((a, b) => new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0));

  return [...history, current];
};

export const createEscalation = async (memberId, payload) => {
  const [response, ownerNameById] = await Promise.all([
    api.post(
      `/api/members/${memberId}/escalations`,
      {
        ...buildEscalationRequestBody(payload),
        creatorId: getCurrentUserId(),
        ...resolutionStampIfResolving(payload),
      },
      { headers: HEADERS },
    ),
    getOwnerNameLookup(),
  ]);
  return mapEscalationResponse(response.data, ownerNameById);
};

// Logs one contact attempt against an escalation - a standalone sub-resource
// (see mapContactAttemptResponse), independent of the escalation's own
// contactStatus field and not part of buildEscalationRequestBody.
export const createContactAttempt = async (memberId, escalationId, payload) => {
  const response = await api.post(
    `/api/members/${memberId}/escalations/${escalationId}/contact-attempts`,
    {
      contactAt: payload.contactAt,
      contactParty: payload.contactParty,
      contactMethod: payload.contactMethod,
    },
    { headers: HEADERS },
  );
  return mapContactAttemptResponse(response.data);
};

// Every contact attempt logged against an escalation, most recent first.
export const fetchContactAttempts = async (memberId, escalationId) => {
  const response = await api.get(
    `/api/members/${memberId}/escalations/${escalationId}/contact-attempts`,
    { headers: HEADERS },
  );
  const list = Array.isArray(response.data) ? response.data : [];
  return list
    .map(mapContactAttemptResponse)
    .sort((a, b) => new Date(b.contactAt) - new Date(a.contactAt));
};

export const updateEscalation = async (memberId, escalationId, payload) => {
  const [response, ownerNameById] = await Promise.all([
    api.put(
      `/api/members/${memberId}/escalations/${escalationId}`,
      {
        ...buildEscalationRequestBody(payload),
        // The assignee is always whoever is currently acting on the escalation.
        assigneeId: getCurrentUserId(),
        ...resolutionStampIfResolving(payload),
      },
      { headers: HEADERS },
    ),
    getOwnerNameLookup(),
  ]);
  return mapEscalationResponse(response.data, ownerNameById);
};
